import React, { useCallback, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { errMessage, inputStyle, primaryBtn, primaryBtnStyle } from '../../components/UI/ink';
import { notificationsApi } from '../../api/notifications';
import TelegramDrawer from '../../components/Settings/TelegramDrawer';
import { DEDUPE_CHOICES, EVENTS, EVENT_LABEL, HOURS, LOG_STATUS, SEVERITY_CHOICES, clock } from '../../components/Settings/model';
import { Notice, SectionHead, Switch, kicker, panel } from '../../components/Settings/ui';

const selCls = 'h-[38px] box-border px-2 rounded-lg border font-mono text-[12.5px]';
const sel = { ...inputStyle, height: 38 };

// What goes where. Telegram is the only channel built today; Email is shown
// so the gap is visible, not as a working column.
export default function RoutingSettings() {
  const { permissions = [] } = useAuth();
  const canEdit = permissions.includes('system:update');
  const [ch, setCh] = useState(undefined);
  const [form, setForm] = useState(null);
  const [log, setLog] = useState({ log: [], last_24h: {} });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [drawer, setDrawer] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await notificationsApi.listChannels();
      const tg = list.find(c => c.type === 'telegram') || null;
      setCh(tg);
      setForm(tg ? { min_severity: tg.min_severity, event_types: tg.event_types, dedupe_minutes: tg.dedupe_minutes, quiet: { ...tg.quiet } } : null);
      setLog(await notificationsApi.logWithStats(30));
      setError(null);
    } catch (err) {
      setCh(null);
      setError(err?.response?.status === 403 ? 'Only administrators can change alert routing.' : errMessage(err, 'Could not load alert routing.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = ch && form && JSON.stringify({ min_severity: ch.min_severity, event_types: ch.event_types, dedupe_minutes: ch.dedupe_minutes, quiet: { enabled: ch.quiet.enabled, start: ch.quiet.start, end: ch.quiet.end } })
    !== JSON.stringify({ min_severity: form.min_severity, event_types: form.event_types, dedupe_minutes: form.dedupe_minutes, quiet: { enabled: form.quiet.enabled, start: form.quiet.start, end: form.quiet.end } });
  const on = (k) => !form || form.event_types === null || form.event_types.includes(k);
  const flip = (k) => setForm(f => {
    const cur = f.event_types === null ? EVENTS.map(([x]) => x) : f.event_types;
    const next = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k];
    return { ...f, event_types: next.length === EVENTS.length ? null : next };
  });
  const setQuiet = (patch) => setForm(f => ({ ...f, quiet: { ...f.quiet, ...patch } }));

  const save = async () => {
    setBusy(true); setError(null); setSaved(false);
    try {
      await notificationsApi.saveTelegram({ chat_id: ch.chat_id, ...form, quiet: { enabled: form.quiet.enabled, start: form.quiet.start, end: form.quiet.end, timezone: form.quiet.timezone } });
      await load();
      setSaved(true);
    } catch (err) {
      setError(errMessage(err, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  };

  const s24 = log.last_24h || {};
  const tgLive = !!ch?.configured && ch.enabled;

  return (
    <div className="flex flex-col gap-[18px]">
      <SectionHead title="Alert routing" sub="What goes to which channel, quiet hours, and what was actually delivered." />
      {error && <Notice>{error}</Notice>}
      {ch === null && !error && (
        <Notice kind="warn">
          No channel is connected, so nothing is sent outside DragonEye yet.{canEdit && <> <button type="button" onClick={() => setDrawer(true)} className="underline" style={{ color: 'var(--ink-link)' }}>Connect Telegram</button>.</>}
        </Notice>
      )}

      <section className="rounded-[14px] border overflow-hidden" style={panel}>
        <div className="overflow-x-auto"><div className="min-w-[560px]">
          <div className="grid grid-cols-[minmax(0,1fr)_132px_132px] gap-2 items-end px-[18px] pt-3.5 pb-2.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
            <span className="font-mono text-[10px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>EVENT</span>
            <span className="flex flex-col items-center gap-[3px] text-center"><span className="text-[13px] font-semibold">Telegram</span>
              <span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{!ch ? 'not connected' : !ch.enabled ? 'paused' : `${form?.min_severity || ch.min_severity} and up`}</span></span>
            <span className="flex flex-col items-center gap-[3px] text-center"><span className="text-[13px] font-semibold" style={{ color: 'var(--ink-faint)' }}>Email</span><span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>Not built yet</span></span>
          </div>
          {EVENTS.map(([k, t, d]) => {
            const lit = on(k);
            return (
              <div key={k} className="grid grid-cols-[minmax(0,1fr)_132px_132px] gap-2 items-center min-h-[54px] px-[18px] py-1 border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
                <span className="flex flex-col gap-0.5"><span className="text-[13.5px] font-medium">{t}</span><span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{d} · <span className="font-mono">{k}</span></span></span>
                <span className="flex justify-center">
                  {ch ? (
                    <button type="button" aria-pressed={lit} aria-label={`${t} to Telegram`} disabled={!canEdit} onClick={() => flip(k)} className="w-11 h-11 flex items-center justify-center disabled:opacity-60">
                      <span className="w-5 h-5 box-border flex items-center justify-center rounded-[5px] border-[1.5px]" style={{ background: lit ? 'var(--accent)' : 'transparent', borderColor: lit ? 'var(--accent)' : 'var(--surf-line-strong)' }}>{lit && <Check className="w-[13px] h-[13px]" strokeWidth={3} color="#0A0A0A" />}</span>
                    </button>
                  ) : <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>–</span>}
                </span>
                <span className="flex justify-center text-[13px]" aria-label="Not available" style={{ color: 'var(--ink-faint)' }}>–</span>
              </div>
            );
          })}
        </div></div>
      </section>

      {ch && form && (
        <>
          <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(280px,1fr))]">
            <section className="flex flex-col gap-2.5 px-[18px] py-4 rounded-[14px] border" style={panel}>
              <span className="text-sm font-semibold">Lowest severity sent</span>
              <div role="group" aria-label="Lowest severity" className="flex gap-1">
                {SEVERITY_CHOICES.map(([id, t, tok]) => {
                  const s = form.min_severity === id;
                  return <button key={id} type="button" aria-pressed={s} disabled={!canEdit} onClick={() => setForm(f => ({ ...f, min_severity: id }))} className="flex-1 h-10 flex items-center justify-center gap-1.5 rounded-lg border text-[13px]" style={{ background: s ? 'var(--surf-raised)' : 'var(--surf-bg)', borderColor: s ? 'var(--accent)' : 'var(--surf-line-strong)' }}><span className="w-[7px] h-[7px] rounded-full" style={{ background: `var(--sev-${tok}-fg)` }} />{t}</button>;
                })}
              </div>
            </section>
            <section className="flex flex-col gap-2.5 px-[18px] py-4 rounded-[14px] border" style={panel}>
              <span className="flex items-center gap-3"><span className="flex-grow flex flex-col gap-[3px]"><span className="text-sm font-semibold">Quiet hours</span><span className="text-[12.5px] leading-snug" style={{ color: 'var(--ink-faint)' }}>Hold medium and high messages and send them as one digest when quiet hours end. Critical always goes out.</span></span>
                <Switch on={form.quiet.enabled} onChange={(v) => setQuiet({ enabled: v })} label="Quiet hours" disabled={!canEdit} /></span>
              <span className="flex flex-wrap items-center gap-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}>
                <label htmlFor="q-from">From</label><select id="q-from" value={form.quiet.start} onChange={e => setQuiet({ start: e.target.value })} disabled={!canEdit} className={selCls} style={sel}>{HOURS.map(h => <option key={h}>{h}</option>)}</select>
                <label htmlFor="q-to">to</label><select id="q-to" value={form.quiet.end} onChange={e => setQuiet({ end: e.target.value })} disabled={!canEdit} className={selCls} style={sel}>{HOURS.map(h => <option key={h}>{h}</option>)}</select>
                <span>{form.quiet.timezone}</span>
              </span>
              {ch.quiet.active && <span className="text-xs" style={{ color: 'var(--sev-med-fg)' }}>Quiet hours are on right now.</span>}
            </section>
            <section className="flex flex-col gap-2.5 px-[18px] py-4 rounded-[14px] border" style={panel}>
              <span className="flex flex-col gap-[3px]"><span className="text-sm font-semibold">Repeats</span><span className="text-[12.5px] leading-snug" style={{ color: 'var(--ink-faint)' }}>The same asset, severity and event type sends once per window, so a flapping alert doesn’t flood the chat.</span></span>
              <span className="flex flex-wrap items-center gap-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}><label htmlFor="r-win">At most once every</label>
                <select id="r-win" value={form.dedupe_minutes} onChange={e => setForm(f => ({ ...f, dedupe_minutes: Number(e.target.value) }))} disabled={!canEdit} className="h-[38px] box-border px-2 rounded-lg border text-[13px]" style={sel}>{DEDUPE_CHOICES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></span>
            </section>
          </div>
          {canEdit && (
            <div className="flex items-center gap-3">
              <button type="button" onClick={save} disabled={!dirty || busy} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Saving…' : 'Save routing'}</button>
              {saved && !dirty && <span className="text-[13px]" style={{ color: 'var(--sev-good-fg)' }}>Saved.</span>}
              {dirty && <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>Unsaved changes</span>}
            </div>
          )}
        </>
      )}

      <section className="rounded-[14px] border overflow-hidden" style={panel}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-[18px] py-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
          <h3 className="m-0 text-[15px] font-semibold">Delivery log</h3>
          <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Last 24 h · {s24.sent || 0} sent · {s24.suppressed || 0} held back as repeats{s24.held ? ` · ${s24.held} held for quiet hours` : ''} · {s24.failed || 0} failed</span>
        </div>
        <div className="overflow-x-auto"><div className="min-w-[600px]">
          <div className={`grid grid-cols-[80px_minmax(0,1fr)_120px_180px] gap-3 px-[18px] py-[9px] border-b ${kicker}`} style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)', fontSize: 10 }}><span>WHEN</span><span>MESSAGE</span><span>EVENT</span><span>RESULT</span></div>
          {(log.log || []).map(l => {
            const [lbl, dot] = LOG_STATUS[l.status] || [l.status, 'var(--ink-faint)'];
            return (
              <div key={l.id} className="grid grid-cols-[80px_minmax(0,1fr)_120px_180px] gap-3 items-center min-h-[44px] px-[18px] py-1 border-b last:border-b-0 text-[13px]" style={{ borderColor: 'var(--surf-line)' }}>
                <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{clock(l.created_at)}</span>
                <span className="truncate">{l.title}</span>
                <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-muted)' }} title={EVENT_LABEL[l.event_type] || l.event_type}>{l.event_type}</span>
                <span className="flex items-center gap-1.5 text-[12.5px] min-w-0" title={l.error || ''}><span className="w-[7px] h-[7px] shrink-0 rounded-full" style={{ background: dot }} /><span className="truncate">{lbl}{l.error ? ` · ${l.error}` : ''}</span></span>
              </div>
            );
          })}
          {!(log.log || []).length && <div className="px-[18px] py-6 text-[13px]" style={{ color: 'var(--ink-muted)' }}>Nothing delivered yet.</div>}
        </div></div>
      </section>
      {!tgLive && ch && <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Telegram is paused, so nothing is being sent. Turn sending on in Integrations › Telegram.</span>}
      <TelegramDrawer open={drawer} onClose={() => setDrawer(false)} onChanged={load} />
    </div>
  );
}
